/**
 * API: onboarding (Sprint 7).
 *
 * Flow: signup → fetchOnboardingContext → selectMunicipality → updateProfile
 * → completeOnboarding. Cada llamada retorna el contexto actualizado.
 */

import type { LegalDocId } from "@/legal/documentos";

import { request } from "./_client";

export interface OnboardingContextDTO {
  has_tenant: boolean;
  active_tenant_id: string | null;
  active_dane_code: string | null;
  active_municipality_name: string | null;
  active_tenant_display_name: string | null;
  onboarded_at: string | null;
  full_name: string | null;
  position: string | null;
  /**
   * True si `users.is_staff = TRUE` (equipo interno de Kaptar — ver
   * ADR-016). Default `false` por retrocompat para clientes/tests
   * que aún no pasen el campo.
   */
  is_staff?: boolean;
  /**
   * Estado operacional del tenant (`active` / `inactive` /
   * `pending_deletion` — ver migración 0033 del BE). Se puebla incluso
   * si el tenant está pausado: el gate de membresía del frontend
   * (`OnboardingGate` + `TenantPausado`) lo necesita para explicarle al
   * usuario qué pasa, en vez de esconderle el tenant y que el wizard se
   * vea vacío (Sprint 25-2). Opcional/nullable por retrocompat, mismo
   * criterio que `is_staff`.
   */
  tenant_status?: string | null;
  /**
   * Estado del embudo comercial (`trial`, `pre_contract`, `active`,
   * `cancelled`, `hibernation`, `awaiting_payment`... — ver migración
   * 0001 del BE). Eje ortogonal a `tenant_status`: de dónde va el
   * contrato, no si el sistema le responde hoy.
   */
  subscription_state?: string | null;
  /** Plan actual del tenant (`esencial` / `pro`). Null si no tiene tenant. */
  tier?: string | null;
  /**
   * Periodicidad de pago (`monthly` legacy / `trimestral` / `semestral`
   * / `annual`). Nullable hasta que admin asigne contrato.
   */
  tier_period?: string | null;
  /**
   * Último día que cubre lo pagado, incluido, como `"YYYY-MM-DD"` del
   * calendario de Colombia (issue #734). Null si nunca pagó o si el
   * vencimiento no está registrado. Leerlo con `fechaDesdeIso`, no con
   * `new Date(...)`, que lo tomaría como medianoche UTC.
   */
  tier_valid_until?: string | null;
  /**
   * Rol del usuario en el tenant activo (`admin_municipal`,
   * `formulador`, `colaborador`, `soporte_vera`).
   */
  member_role?: string | null;
  /** Memberships activas del tenant (todos los usuarios, no solo el actual). */
  seats_used?: number | null;
  /**
   * Expedientes que ocupan cupo del plan (borrador, radicado, viable).
   * Opcional: llega desde #912 (issue #878).
   */
  active_projects_used?: number | null;
  /**
   * Lo que cubre cada plan, no solo el actual. `active_projects: null` es
   * ilimitado. Opcional: llega desde #912; sin el campo, la pagina de
   * renovar no avisa (el backend igual bloquea el pago).
   */
  cupos_por_plan?: Record<"esencial" | "pro", { users: number; active_projects: number | null }>;
  /** Cupo máximo de usuarios del tier actual. */
  seats_limit?: number | null;
  /**
   * True cuando el usuario no es staff, no tiene ninguna membership activa
   * y al menos una le fue revocada: lo quitaron del equipo. No es un
   * usuario nuevo (`has_tenant=false` a secas): no se le abre el wizard,
   * se le cierra la sesión y `/login` explica el motivo (7-sep-2026).
   * Opcional por retrocompat con el backend anterior a la migración 0038.
   */
  access_revoked?: boolean;
  /**
   * Documentos legales cuya versión vigente el funcionario no ha aceptado
   * (`terminos`, `privacidad`). Si no está vacío y ya terminó el wizard,
   * `OnboardingGate` lo manda a `/actualizacion-legal`. Siempre vacío para
   * el staff. Opcional por retrocompat con el backend anterior a la
   * migración 0039: sin el campo, no hay nada pendiente.
   */
  legal_pending?: LegalDocId[];
  /** Versión vigente de cada documento según el backend. */
  legal_versions?: Partial<Record<LegalDocId, string>>;
}

export function fetchOnboardingContext(): Promise<OnboardingContextDTO> {
  return request<OnboardingContextDTO>("/onboarding/context");
}

export function selectMunicipality(daneCode: string): Promise<OnboardingContextDTO> {
  return request<OnboardingContextDTO>("/onboarding/select-municipality", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ dane_code: daneCode }),
  });
}

export function updateOnboardingProfile(payload: {
  full_name: string;
  position: string;
  phone: string;
}): Promise<OnboardingContextDTO> {
  return request<OnboardingContextDTO>("/onboarding/profile", {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function completeOnboarding(): Promise<OnboardingContextDTO> {
  return request<OnboardingContextDTO>("/onboarding/complete", {
    method: "POST",
  });
}
